import React from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';

interface MetricCardProps {
  title: string;
  current: number;
  previous: number;
  unit: string;
  icon: React.ReactNode;
  color: string;
}

export function MetricCard({ title, current, previous, unit, icon, color }: MetricCardProps) {
  const difference = ((current - previous) / previous) * 100;
  const isIncrease = current > previous;

  const getColorClasses = (colorClass: string) => {
    switch (colorClass) {
      case 'bg-primary/20 text-primary':
        return {
          icon: 'text-primary',
          glow: 'bg-primary/20',
          wrapper: 'bg-primary/10'
        };
      case 'bg-orange-500/20 text-orange-500':
        return {
          icon: 'text-orange-500',
          glow: 'bg-orange-500/20',
          wrapper: 'bg-orange-500/10'
        };
      case 'bg-purple-500/20 text-purple-500':
        return {
          icon: 'text-purple-500',
          glow: 'bg-purple-500/20',
          wrapper: 'bg-purple-500/10'
        };
      case 'bg-green-500/20 text-green-500':
        return {
          icon: 'text-green-500',
          glow: 'bg-green-500/20',
          wrapper: 'bg-green-500/10'
        };
      case 'bg-red-500/20 text-red-500':
        return {
          icon: 'text-red-500',
          glow: 'bg-red-500/20',
          wrapper: 'bg-red-500/10'
        };
      default:
        return {
          icon: 'text-primary',
          glow: 'bg-primary/20',
          wrapper: 'bg-primary/10'
        };
    }
  };

  const colorClasses = getColorClasses(color);

  return (
    <div className="theme-card p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-muted-foreground">{title}</span>
        <div className={`relative w-8 h-8 rounded-lg ${colorClasses.wrapper} flex items-center justify-center`}>
          <div className={`relative z-10 ${colorClasses.icon}`}>
            {icon}
          </div>
          <div className={`absolute inset-0 ${colorClasses.glow} blur-xl rounded-full animate-pulse`} />
        </div>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold">{current.toLocaleString('en-US', { maximumFractionDigits: 1 })}</span>
        <span className="text-sm text-muted-foreground">{unit}</span>
      </div>
      <div className={`flex items-center gap-1 text-sm ${isIncrease ? 'text-success' : 'text-destructive'}`}>
        {isIncrease ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
        <span>{Math.abs(difference).toFixed(1)}% vs yesterday</span>
      </div>
    </div>
  );
}